/**
 * Utility functions for UI components automatically added by ShadCN and used in a few of our frontend components and blocks.
 *
 * Other functions may be exported from here in the future or by installing other shadcn components.
 */

import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * Wilson defines its own font-size scale in globals.css `@theme`:
 *   --text-body / -md / -sm / -xs          →  text-body, text-body-md, …
 *   --text-display-md … --text-display-5xl →  text-display-md, … text-display-5xl
 *
 * tailwind-merge does not know about these. Out of the box it classifies
 * `text-body-md` as a *text-colour* class (the `text-{color}` group), putting it in the
 * same conflict group as `text-foreground` / `text-white`. The later class then wins and
 * the font-size is silently dropped:
 *
 *   twMerge('text-body-md text-foreground')  →  'text-foreground'   ❌ size lost
 *
 * That silently destroyed the responsive Body/Headings line-heights anywhere a size and a
 * colour were merged together. It surfaced in the header nav, where menu items rendered at
 * 24px on mobile instead of the 20px the Figma type scale specifies.
 *
 * Registering the scale under `font-size` restores correct behaviour: sizes conflict only
 * with other sizes, colours only with other colours.
 */
const TEXT_SIZES = [
  'body',
  'body-md',
  'body-sm',
  'body-xs',
  'display-md',
  'display-lg',
  'display-xl',
  'display-2xl',
  'display-3xl',
  'display-4xl',
  'display-5xl',
]

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: TEXT_SIZES }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
